import { merge } from 'es-toolkit'
import { useBoolean } from 'minimal-shared/hooks'

import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useState } from 'react'

import { AccountDrawer } from '../components/account-drawer'
import { MenuButton } from '../components/menu-button'
import { layoutClasses } from '../core/classes'
import { HeaderSection } from '../core/header-section'
import { LayoutSection } from '../core/layout-section'
import { MainSection } from '../core/main-section'
import { NavMobile } from './nav-mobile'
import { NavVertical } from './nav-vertical'
// import { LanguagePopover } from '../components/language-popover'
import PoweredByWrapper from 'wrappers/poweredBy/PoweredByWrapper'
import { defaultSettings } from '../../theme/settings-config'
import BalanceWithAnimation from '../../components/shared/overview/BalanceWithAnimation'
import MembershipBadge from '../../components/shared/overview/MembershipBadge'
import { extractInitials } from '../../util/wallet/common.helpers'
import ThemeSwitchButton from '../components/theme-switch-button'
import { TransactionHistoryButton } from '../components/transaction-history-button'
import {
  buildAccountMenu,
  buildHomeMenu,
  buildNavData
} from '../nav-config-dashboard'
import { DashboardContent } from './content'
import { dashboardLayoutVars, dashboardNavColorVars } from './css-vars'
import ConnectWalletNotification from './ConnectWalletNotification'
import { shouldShowWalletNotification } from '../../util/app/app.helpers'

export function DashboardLayout({
  sx,
  cssVars,
  children,
  slotProps,
  layoutQuery = 'lg',
  tokenProfile: { symbol },
  signOut,
  isQuestsEnabled,
  mainProfile: {
    isNavMini,
    brandAppConfig,
    previousUserPoints,
    currentUserPoints,
    userTier,
    exchangeMemberships
  },
  stakingProfile: { availableQBX },
  onToggleNav,
  accountProfile: { firstName, lastName, email, authId, insertedAt },
  onWalletClick,
  connectedWallet,
  onOpenCOEStore,
  onOpenNoQBX
}) {
  const theme = useTheme()

  const { t } = useTranslation()

  const navVars = dashboardNavColorVars(theme, defaultSettings.navColor)

  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean()

  const [notificationBarHeight, setNotificationBarHeight] = useState(0)
  const notificationRef = useRef(null)
  const showTopBarNotification =
    shouldShowWalletNotification(insertedAt) &&
    ((!connectedWallet && availableQBX <= 0) ||
      (connectedWallet && availableQBX !== null && availableQBX <= 0))
  useEffect(() => {
    let observer
    if (showTopBarNotification) {
      observer = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.contentRect.height > 0) {
            setNotificationBarHeight(entry.contentRect.height + 15)
          } else {
            setNotificationBarHeight(0)
          }
        }
      })
      if (notificationRef.current) {
        observer.observe(notificationRef.current)
      }
    } else {
      setNotificationBarHeight(0)
    }
    return () => {
      if (observer) {
        if (notificationRef.current) {
          observer.unobserve(notificationRef.current)
        }
        observer.disconnect()
      }
    }
  }, [connectedWallet, showTopBarNotification])

  const userInitials = extractInitials(firstName, lastName)
  const navData =
    slotProps?.nav?.data ??
    buildNavData({
      t,
      symbol,
      isQuestsEnabled,
      cashbackEarnOption: brandAppConfig.cashbackEarnOption,
      onWalletClick,
      connectedWallet,
      authId,
      onOpenCOEStore
    })

  const isNavVertical = isNavMini || true

  const renderHeader = () => {
    const headerSlotProps = {
      container: {
        maxWidth: false,
        sx: {
          mt: `${notificationBarHeight}px`,
          ...(isNavVertical && { px: { [layoutQuery]: 5 } })
        }
      }
    }

    const headerSlots = {
      topArea: null,
      bottomArea: null,
      leftArea: (
        <>
          <MenuButton
            onClick={onOpen}
            sx={{
              mr: 1,
              ml: -1,
              [theme.breakpoints.up(layoutQuery)]: { display: 'none' }
            }}
          />
          <NavMobile
            data={navData}
            open={open}
            onClose={onClose}
            cssVars={navVars.section}
            slots={{ bottomArea: <PoweredByWrapper /> }}
            sx={{ mt: `${notificationBarHeight}px` }}
          />
          <BalanceWithAnimation
            t={t}
            symbol={symbol}
            previousUserPoints={previousUserPoints}
            currentUserPoints={currentUserPoints}
          />
          <MembershipBadge t={t} userTier={userTier} />
        </>
      ),
      rightArea: (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0, sm: 0.75 }
          }}
        >
          {/* <LanguagePopover data={[]} /> */}
          <ThemeSwitchButton />
          <TransactionHistoryButton />
          <AccountDrawer
            data={[buildHomeMenu(t), ...buildAccountMenu({ t })]}
            userInitials={userInitials}
            firstName={firstName}
            lastName={lastName}
            email={email}
            t={t}
            signOut={signOut}
            userTier={userTier}
            authId={authId}
            notificationBarHeight={notificationBarHeight}
            exchangeMemberships={exchangeMemberships}
          />
        </Box>
      )
    }

    return (
      <HeaderSection
        layoutQuery={layoutQuery}
        disableElevation={isNavVertical}
        {...slotProps?.header}
        slots={{ ...headerSlots, ...slotProps?.header?.slots }}
        slotProps={merge(headerSlotProps, slotProps?.header?.slotProps ?? {})}
        sx={slotProps?.header?.sx}
      />
    )
  }

  const renderSidebar = () => (
    <NavVertical
      data={navData}
      isNavMini={isNavMini}
      layoutQuery={layoutQuery}
      cssVars={navVars.section}
      onToggleNav={onToggleNav}
      slots={{ bottomArea: <PoweredByWrapper /> }}
      sx={{ mt: `${notificationBarHeight}px` }}
    />
  )

  const renderFooter = () => null

  const renderMain = () => (
    <MainSection {...slotProps?.main}>
      <DashboardContent maxWidth='xl'>{children}</DashboardContent>
    </MainSection>
  )
  return (
    <>
      {showTopBarNotification && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: `${notificationBarHeight}px`,
            zIndex: (theme) => theme.zIndex.modal - 1,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none'
          }}
        >
          <Box sx={{ pointerEvents: 'auto' }}>
            <ConnectWalletNotification
              onWalletClick={onWalletClick}
              symbol={symbol}
              notifyRef={notificationRef}
              connectedWallet={connectedWallet}
              onBannerAction={onOpenNoQBX}
            />
          </Box>
        </Box>
      )}
      <LayoutSection
        /** **************************************
         * @Header
         *************************************** */
        headerSection={renderHeader()}
        /** **************************************
         * @Sidebar
         *************************************** */
        sidebarSection={renderSidebar()}
        /** **************************************
         * @Footer
         *************************************** */
        footerSection={renderFooter()}
        /** **************************************
         * @Styles
         *************************************** */
        cssVars={{
          ...dashboardLayoutVars(theme),
          ...navVars.layout,
          ...cssVars
        }}
        sx={[
          {
            [`& .${layoutClasses.sidebarContainer}`]: {
              [theme.breakpoints.up(layoutQuery)]: {
                pl: isNavMini
                  ? 'var(--layout-nav-mini-width)'
                  : 'var(--layout-nav-vertical-width)',
                transition: theme.transitions.create(['padding-left'], {
                  easing: 'var(--layout-transition-easing)',
                  duration: 'var(--layout-transition-duration)'
                })
              }
            }
          },
          ...(Array.isArray(sx) ? sx : [sx])
        ]}
      >
        {renderMain()}
      </LayoutSection>
    </>
  )
}
